import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as restaurantsApi from "@api/restaurants.api";
import type { Restaurant } from "@/types";

interface RestaurantsState {
  items: Restaurant[];
  loading: boolean;
}

const initialState: RestaurantsState = {
  items: [],
  loading: false,
};

export const fetchRestaurants = createAsyncThunk(
  "restaurants/fetchAll",
  async () => {
    return await restaurantsApi.getAll();
  },
);

export const createRestaurant = createAsyncThunk(
  "restaurants/create",
  async (data: {
    name: string;
    categoryId: string;
    url?: string;
    location?: string;
    imageUrl?: string;
  }) => {
    return await restaurantsApi.create(data);
  },
);

export const updateRestaurant = createAsyncThunk(
  "restaurants/update",
  async ({
    id,
    data,
  }: {
    id: string;
    data: {
      name?: string;
      categoryId?: string;
      url?: string;
      location?: string;
      imageUrl?: string;
    };
  }) => {
    return await restaurantsApi.update(id, data);
  },
);

export const deleteRestaurant = createAsyncThunk(
  "restaurants/delete",
  async (id: string) => {
    await restaurantsApi.remove(id);
    return id;
  },
);

const restaurantsSlice = createSlice({
  name: "restaurants",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRestaurants.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRestaurants.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchRestaurants.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createRestaurant.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(updateRestaurant.fulfilled, (state, action) => {
        const idx = state.items.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(deleteRestaurant.fulfilled, (state, action) => {
        state.items = state.items.filter((r) => r.id !== action.payload);
      });
  },
});

export default restaurantsSlice.reducer;
